<template>
  <div
    class="sidebar"
    ref="sidebar">

    <div
      class="sidebar-logo"
      @click="logoClick">
      <img src="@/assets/logo-black-no-text.png">
      <div class="logo-text">Byblio</div>
    </div>

    <div class="divider" />

    <div
      class="sidebar-item sidebar-home"
      :class="{
        'sidebar-item-active': homeButtonActive,
      }"
      @click="$navigateTo({ name: 'Home' })">
      <i class="mdi mdi-home" />
      Home
    </div>

    <div class="divider" />


    <div class="sidebar-bycollections">
      <div class="sidebar-header">
        Collections
        <span
          class="sidebar-manage-collections"
          @click="$navigateTo({ name: 'CollectionManagement' })"> - manage collections</span>
      </div>
      <div class="sidebar-collections">
        <ul class="navigation-list">
          <CollectionNavigationItems
            :collections="allCollections"
            :isRoot="true" />
        </ul>
      </div>
    </div>

    <div class="divider" />

    <div class="sidebar-bystars">
      <div class="sidebar-header">
        Cards by stars
        <span
          class="sidebar-toggle-select-all"
          @click="toggleSelectAllStars"> - {{ selectedStars.length === 3 ? 'Deselect all' : 'Select all' }}</span>
      </div>
      <div class="sidebar-bystars-list">
        <div
          v-for="n in 3"
          :key="n"
          class="stars-navigation"
          :class="{
            'stars-navigation-selected': selectedStars.includes(n),
            'stars-navigation-active': selectedStars.includes(n) && $route.name === 'StarList',
          }"
          @click="handleStarClick(n)">
          <i
            :class="{
              'mdi mdi-star-outline': n === 1,
              'mdi mdi-star-half': n === 2,
              'mdi mdi-star': n === 3,
            }" />
        </div>
      </div>
    </div>

    <div class="sidebar-bykeywords">
      <div class="sidebar-header">
        Cards by keyword
        <span
          class="sidebar-toggle-select-all"
          v-show="keywords.length"
          @click="toggleSelectAllKeywords"> - {{ selectedKeywords.length === keywords.length ? 'Deselect all' : 'Select all' }}</span>
      </div>
      <div class="sidebar-bykeywords-list">
        <div
          v-for="keyword in keywords"
          :key="keyword"
          class="keyword-navigation"
          :class="{
            'keyword-navigation-selected': selectedKeywords.includes(keyword),
            'keyword-navigation-active': selectedKeywords.includes(keyword) && $route.name === 'KeywordList',
          }"
          @click="handleKeywordClick(keyword)">
          {{ keyword }}
        </div>
        <div
          v-if="!keywords.length"
          class="keyword-navigation"
          style="cursor: default; border: none">
          <em>No keywords found</em>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import { mapState, mapGetters, mapMutations } from 'vuex';
import CollectionNavigationItems from '@/components/AppView/Sidebar/CollectionNavigationItems.vue';

export default {
  name: 'Sidebar',
  components: {
    CollectionNavigationItems,
  },
  computed: {
    ...mapState({
      collectionNodes: state => state.collections.collectionNodes,
      selectedCollection: state => state.collections.selectedCollection,
      name: state => state.users.userInfo.name,
      firstName: state => state.users.userInfo.firstName,
      lastName: state => state.users.userInfo.lastName,
      email: state => state.users.userInfo.email,
      introStep: state => state.appState.introStep,
      numberOfSteps: state => state.appState.numberOfSteps,
      sidebarWidth: state => state.appState.localSettings.sidebarWidth,
      lastKeywordList: state => state.appState.lastKeywordList,
      lastStarList: state => state.appState.lastStarList,
    }),
    ...mapGetters({
      keywords: 'cards/keywords',
    }),
    homeButtonActive() {
      const { name } = this.$route;
      const homeButtonIsActiveFor = ['Home', 'AccountManagement', 'CollectionManagement', 'HowToUse'];
      return homeButtonIsActiveFor.includes(name);
    },
    allCollections() {
      return (this.collectionNodes && this.collectionNodes['0'].children) || [];
    },
    sidebarAccountText() {
      if (this.name) return this.name;
      if (this.firstName && this.lastName) return `${this.firstName} ${this.lastName}`;
      if (this.firstName) return this.firstName;
      if (this.lastName) return this.lastName;
      return this.email;
    },
    selectedKeywords() {
      if (this.lastKeywordList) {
        const selKeywords = this.lastKeywordList.split('/');
        // First two items are irrelevant
        selKeywords.shift();
        selKeywords.shift();
        return selKeywords;
      }
      return [];
    },
    selectedStars() {
      if (this.lastStarList) {
        const selStars = this.lastStarList.split('/');
        // First two items are not the specified stars
        selStars.shift();
        selStars.shift();
        // Return in number format
        return selStars.map(stars => stars * 1);
      }
      return [];
    },
  },
  methods: {
    ...mapMutations({
      ExpandCollection: 'appState/ExpandCollection',
      SaveKeywordList: 'appState/SaveKeywordList',
      SaveStarList: 'appState/SaveStarList',
    }),
    toggleSelectAllStars() {
      if (this.selectedStars.length === 3) {
        this.SaveStarList(null);

        // Navigate the last non-starlist page in navigationHistory
        const path = this.$store.state.appState.navigationHistory.find(val => !val.includes('/star'));
        this.$navigateTo({ path });
      }
      else {
        const path = '/stars/1/2/3';
        if (this.$route.name === 'StarList') this.SaveStarList(path);
        else this.$navigateTo({ path });
      }
    },
    toggleSelectAllKeywords() {
      if (this.selectedKeywords.length === this.keywords.length) {
        this.SaveKeywordList(null);

        // Navigate the last non-keywordlist page in navigationHistory
        const path = this.$store.state.appState.navigationHistory.find(val => !val.includes('/keyword'));
        this.$navigateTo({ path });
      }
      else {
        let path = '/keyword';
        this.keywords.forEach((keyword) => {
          path += `/${keyword}`;
        });
        if (this.$route.name === 'KeywordList') this.SaveKeywordList(path);
        else this.$navigateTo({ path });
      }
    },
    handleStarClick(stars) {
      if (this.$route.name === 'StarList' && this.selectedStars.includes(stars)) this.$removeStarFromList(stars);
      else if (this.selectedStars.includes(stars)) this.$navigateTo({ path: this.lastStarList });
      else this.$addStarToList(stars);
    },
    handleKeywordClick(keyword) {
      if (this.$route.name === 'KeywordList' && this.selectedKeywords.includes(keyword)) this.$removeKeywordFromList(keyword);
      else if (this.selectedKeywords.includes(keyword)) this.$navigateTo({ path: this.lastKeywordList });
      else this.$addKeywordToList(keyword);
    },
    logoClick() {
      let name = null;
      if (this.name) name = this.name; // eslint-disable-line
      else if (this.firstName && this.lastName) name = `${this.firstName} ${this.lastName}`;
      else if (this.firstName) name = this.firstName;
      else if (this.lastName) name = this.lastName;

      this.$buefy.toast.open({
        message: `Glad to see you${name ? `, ${name}` : ''}!`,
        type: 'is-secondary-dark',
      });
    },
    async expandToSelectedCollection() {
      let collectionToExpand = this.collectionNodes[this.selectedCollection] && this.collectionNodes[this.selectedCollection].parent;
      if (!collectionToExpand) return;

      while (collectionToExpand.id !== 0) {
        this.ExpandCollection(collectionToExpand.id);

        // Replace the collectionToExpand to traverse the tree structure upwards
        collectionToExpand = collectionToExpand.parent;
      }

      // Scroll selected collection into view (this does not always work properly, but for now good enough)
      await this.$nextTick();
      await this.$nextTick();
      const el = document.getElementsByClassName('selected-collection-item')[0];

      const rect = el.getBoundingClientRect();
      const sidebarCollectionsContainer = document.getElementsByClassName('sidebar-collections')[0];
      const containerRect = sidebarCollectionsContainer.getBoundingClientRect();

      const elementIsInView = (
        rect.top >= (0 + containerRect.top)
        && rect.bottom <= (containerRect.top + sidebarCollectionsContainer.clientHeight + 4) // the +4 is a buffer, else it scrolls elements that are in fact visible
      );


      if (!elementIsInView) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    },
  },
  mounted() {
    this.expandToSelectedCollection();
  },
  watch: {
    selectedCollection() {
      this.expandToSelectedCollection();
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~styles/colors';
@import '~styles/layouts';
@import '~styles/fonts';

.sidebar {
  display: flex;
  flex-direction: column;
  flex-wrap: nowrap;
  justify-content: flex-start;
  height: 100vh;
  max-height: 100vh;
  width: 100%;
  transition: width 0.3s;
  overflow: hidden;
}
.sidebar-logo {
  position: sticky;
  top: 0.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 3rem;
  min-height: 3rem;
  width: 100%;
  margin: 0.25rem auto -0.25rem;
  cursor: pointer;
  font-family: $quattrocento;
  max-width: 14vw;
  background-color: #f5f5f6;
}
.sidebar-logo img {
  height: 2.5rem;
}
.logo-text {
  display: inline-block;
  height: 2.5rem;
  position: relative;
  font-size: 1.2rem;
  line-height: 2.5rem;
  letter-spacing: 2px;
  left: 0.5rem;
}
.sidebar-home {
  flex: 0 0 auto;
}
.divider {
  height: 0;
  width: 100%;
  margin: 0.75rem 0;
  border-bottom: 1px solid #dbdbdb;
  flex: 0 0 auto;
}
.divider:first-child {
  margin: 0 0 0.75rem;
}
.sidebar-header {
  font-variant: small-caps;
  font-weight: 600;
  font-size: 0.8rem;
  color: $secondary;
  padding-left: 0.25rem;
}
.sidebar-header > .mdi {
  opacity: 0;
}
.sidebar-header:hover > .mdi {
  opacity: 1;
}
.sidebar-item {
  padding: 0.25rem 0 0.25rem 0.5rem;
  cursor: pointer;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  background-color: rgba($secondary-light, 0);
  transition: background-color 0.1s;
}
.sidebar-item i {
  margin-right: 0.5rem;
}
.sidebar-item-active {
  background-color: $secondary-light;
}
.navigation-list {
  padding: 0.5rem 0;
  list-style: none;
}
.sidebar-collections {
  height: calc(100% - 1.5rem);
  overflow-y: auto;
}
.sidebar-bykeywords-list {
  height: calc(100% - 1.5rem);
  flex: 0 1 auto;
  line-height: 1rem;
  overflow-y: auto;
}
.sidebar-bystars-list {
  overflow: hidden;
}
.sidebar-bystars,
.sidebar-bykeywords,
.sidebar-bycollections {
  user-select: none;
}
.sidebar-bystars {
  flex: 0 0 auto;
}
.sidebar-bycollections {
  max-height: 24rem;
  flex: 0 1 auto;
  overflow: hidden;
}
.sidebar-bykeywords {
  height: 20vh;
  flex: 0 1 auto;
}
.keyword-navigation,
.stars-navigation {
  display: inline-block;
  margin: 0 0.25rem;
  padding: 0.15rem 0.25rem 0.15rem 0.25rem;
  cursor: pointer;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 0.85rem;
  font-weight: 300;
  font-family: $headerFont;
  border-radius: 1rem;
  border: 1px solid transparent;
  background-color: rgba($secondary-light, 0);
  transition: background-color 0.1s, border 0.1s;
}
.stars-navigation {
  font-size: 1rem;
}
.keyword-navigation:hover,
.stars-navigation:hover {
  // border: 1px solid $secondary-light;
  background: $primary;
}
.keyword-navigation-active,
.stars-navigation-active {
  background-color: $secondary-light !important;
}
.keyword-navigation-selected,
.stars-navigation-selected {
  background-color: $primary;
}
.sidebar-toggle-select-all,
.sidebar-manage-collections {
  font-variant: normal;
  font-size: 0.65rem;
  font-weight: normal;
  font-family: $bodyFont;
  opacity: 0;
  transition: opacity 0.2s;
  cursor: pointer;
}
.sidebar-toggle-select-all:hover,
.sidebar-manage-collections:hover {
  color: $secondary-dark;
}
.sidebar-bystars:hover .sidebar-toggle-select-all,
.sidebar-bykeywords:hover .sidebar-toggle-select-all,
.sidebar-bycollections:hover .sidebar-manage-collections {
  opacity: 1;
}
</style>
